import { Alert, Badge, Button, Card, Group, Select, SimpleGrid, Stack, Text, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { ApiError, NetworkError } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair, OverShortPair } from '../../components/CashFigures';
import { useLocationId } from '../../hooks/useSeedLocation';
import { enumColor } from '../../lib/enums';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { intlLocale, type PortalLocale, portalLocale } from '../../lib/locale';
import { notifyDone, notifyProblem } from '../../lib/notify';
import { businessDayCloseCopy } from './businessDayCloseCopy';

/**
 * A business day, and the moment it stops moving.
 *
 * A trading night is the unit everything in this business is measured in — it rolls at four in the
 * morning, so a drawer counted at one belongs to the evening before it, and so does everything rung
 * on it. There was no screen anywhere that named one. The doors to close a night and to open it
 * again have existed since the first month of this project and nothing in the office could reach
 * either, so there was **no moment at which last night's figures stopped**: a night that had been
 * counted, reported and handed to an accountant went on accepting sales.
 *
 * Closing it is what makes that stop, and it is enforced rather than decorative — the rule lives in
 * one place on the server and every writer asks it. Three things are true of a closed night, and the
 * screen says all three because the second and third are the ones that surprise people:
 *
 *   - nothing NEW can be put on it. A tab, a payment, a bill closed onto that night: refused,
 *     naming the night and saying to reopen it;
 *   - anything that HAPPENED while it was open still lands. A register that was offline all evening
 *     drains on Thursday and its Tuesday takings are Tuesday's, not an attempt to rewrite history;
 *   - a lawful invoice is never refused at all. The paper is already in the guest's hand and its
 *     number came out of a government range; refusing it would leave a hole somebody has to explain
 *     to the SAR.
 *
 * Reopening asks for a reason in the owner's own words and is recorded against the night, because
 * the question an accountant asks three months later is not whether it was reopened but why.
 */

interface BusinessDay {
  id: string;
  businessDate: string;
  status: 'open' | 'closed' | 'reopened';
  openedAt: string | null;
  closedAt: string | null;
  reopenReason: string | null;
}

interface BusinessDayPage {
  items: BusinessDay[];
  nextBefore: string | null;
}

interface DaySummarySession {
  id: string;
  registerName: string;
  status: 'open' | 'closed';
  overShort: CashPair | null;
}

/**
 * One thing that reached this night after it was closed off.
 *
 * `arrivedLate` separates the two ways that happens, and they read very differently to the person
 * who counted the drawers. A register syncing what it rang while the night was open is the system
 * working exactly as intended; an invoice printed against the night afterwards is a legal document
 * the close was never allowed to turn away. Neither is somebody rewriting history — so the screen
 * says which it was, or the list reads as a list of things that went wrong.
 */
interface LandedAfterClose {
  at: string;
  by: string | null;
  what: string;
  arrivedLate: boolean;
  /** Minor units, signed. Null on something that moves no money. */
  amountMinor: number | null;
  currency: string | null;
}

interface DaySummary {
  sessions: DaySummarySession[];
  closingWork: {
    ordinaryTabs: number;
    awaitingPayment: number;
  };
  overShortTotal: CashPair;
  /**
   * The whole-unit round-up cash settles to, summed over the night's bills and shown per currency.
   *
   * It is its own line and it is not tax and not a discount: it adjusts what was handed over, never
   * what was sold. This is the only screen in the office that shows it at all.
   */
  rounding: CashPair;
}

const BUSINESS_DAYS_PATH = '/admin/v1/operations/business-days';

/** Load the complete history so an unfinished older night cannot disappear behind a page boundary. */
async function loadBusinessDays(locationId: string): Promise<BusinessDayPage> {
  const items = new Map<string, BusinessDay>();
  const seenCursors = new Set<string>();
  let before: string | null = null;

  for (;;) {
    const cursor: string = before ? `&before=${encodeURIComponent(before)}` : '';
    const page: BusinessDayPage = await rawApi<BusinessDayPage>(
      'GET',
      `${BUSINESS_DAYS_PATH}?locationId=${encodeURIComponent(locationId)}&limit=31${cursor}`,
    );
    for (const day of page.items) items.set(day.id, day);
    if (!page.nextBefore) return { items: [...items.values()], nextBefore: null };
    if (seenCursors.has(page.nextBefore)) {
      throw new Error('Business day pagination repeated a cursor');
    }
    seenCursors.add(page.nextBefore);
    before = page.nextBefore;
  }
}

function Figure({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <Card withBorder>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text component="div" fw={700} size="lg">
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed" mt={2}>
          {hint}
        </Text>
      )}
    </Card>
  );
}

function problemCode(error: unknown): string | undefined {
  if (error instanceof NetworkError) return 'network';
  return error instanceof ApiError ? error.code : undefined;
}

export function TheNight({ locale = portalLocale() }: { locale?: PortalLocale }) {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [chosenDayId, setChosenDayId] = useState<string | null>(null);
  const text = businessDayCloseCopy(locale);
  const dateLocale = intlLocale(locale);
  const varianceLabels = {
    exact: text.exact,
    short: text.short,
    over: text.over,
    notCounted: text.notReconciled,
  };

  /*
   * The business day the bar is on right now, from the bar's own clock and cutoff rather than the
   * browser's calendar. Its ids are ULIDs that should never have to reach a screen by hand.
   *
   * This read materialises the row if the night has not been named yet, which is deliberate on the
   * server's side and worth knowing here: opening this screen at half past four in the morning
   * starts the current business day in the same breath as the first register would have.
   */
  const currentDay = useQuery({
    queryKey: listKey(`${BUSINESS_DAYS_PATH}/current`, { locationId }),
    queryFn: () =>
      rawApi<BusinessDay>(
        'GET',
        `${BUSINESS_DAYS_PATH}/current?locationId=${encodeURIComponent(locationId ?? '')}`,
      ),
    enabled: Boolean(locationId),
  });

  /*
   * A drawer counted after the 04:00 cutoff still belongs to the prior business day. The current
   * endpoint quite correctly names the new day by then; it just cannot also name the one the
   * closing Bartender finished. Load every page into the picker so an older unfinished night cannot
   * be hidden by newer closed history, without moving a tab or changing the date of any sale.
   *
   * Loading after `current` guarantees the new day is present even when no register has used it
   * yet. The merge below still keeps the current row visible if the history request fails.
   */
  const days = useQuery({
    queryKey: listKey(BUSINESS_DAYS_PATH, { locationId, limit: 31 }),
    queryFn: () => loadBusinessDays(locationId ?? ''),
    enabled: Boolean(locationId) && currentDay.isSuccess,
  });

  const availableDays = [...(days.data?.items ?? [])];
  if (currentDay.data && !availableDays.some((candidate) => candidate.id === currentDay.data?.id)) {
    availableDays.push(currentDay.data);
  }
  availableDays.sort((left, right) => right.businessDate.localeCompare(left.businessDate));

  // Put an unfinished prior day in front of the Manager before the newly materialised current day.
  // The current choice stays clearly marked in the picker and remains one gesture away.
  const pendingPriorDay = availableDays.find(
    (candidate) => candidate.id !== currentDay.data?.id && candidate.status !== 'closed',
  );
  const businessDayId = chosenDayId ?? pendingPriorDay?.id ?? currentDay.data?.id;
  const day = availableDays.find((candidate) => candidate.id === businessDayId) ?? currentDay.data;
  const reviewingPriorDay = Boolean(day && currentDay.data && day.id !== currentDay.data.id);
  const dayOptions = availableDays.map((candidate) => ({
    value: candidate.id,
    label: [
      formatDate(candidate.businessDate, dateLocale),
      text.status[candidate.status],
      candidate.id === currentDay.data?.id ? text.currentDay : null,
    ]
      .filter(Boolean)
      .join(' · '),
  }));

  const summary = useQuery({
    queryKey: listKey('/admin/v1/cash/day-summary', { businessDayId, locationId }),
    queryFn: () =>
      rawApi<DaySummary>(
        'GET',
        `/admin/v1/cash/day-summary?businessDayId=${encodeURIComponent(businessDayId ?? '')}&locationId=${encodeURIComponent(locationId ?? '')}`,
      ),
    enabled: Boolean(businessDayId) && Boolean(locationId),
  });

  /*
   * WHAT CAME IN AFTER THE CLOSE, which is the half of this that makes the other half honest.
   *
   * The paragraph above promises the owner that a late arrival and an invoice are "recorded against
   * the business day so you can see what came in late". Without this read that sentence is a promise the
   * screen cannot keep — the same shape of defect as a Confirmed column with nothing behind it.
   *
   * Asked for on a business day that is not open, not only on a closed one: a day that has been reopened
   * may well be carrying rows from the hours it spent closed, and those are exactly the ones
   * somebody is looking for when they open this screen.
   */
  const landed = useQuery({
    queryKey: listKey(`${BUSINESS_DAYS_PATH}/landed-after-close`, { businessDayId }),
    queryFn: () =>
      rawApi<LandedAfterClose[]>('GET', `${BUSINESS_DAYS_PATH}/${businessDayId}/landed-after-close`),
    enabled: Boolean(businessDayId) && day?.status !== 'open',
  });

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: listKey(`${BUSINESS_DAYS_PATH}/current`, { locationId }),
    });
    void queryClient.invalidateQueries({
      queryKey: listKey(BUSINESS_DAYS_PATH, { locationId, limit: 31 }),
    });
    void queryClient.invalidateQueries({
      queryKey: listKey('/admin/v1/cash/day-summary', { businessDayId, locationId }),
    });
    void queryClient.invalidateQueries({
      queryKey: listKey(`${BUSINESS_DAYS_PATH}/landed-after-close`, { businessDayId }),
    });
  };

  const close = useMutation({
    mutationFn: () => rawApi('POST', `${BUSINESS_DAYS_PATH}/${businessDayId}/close`, {}),
    onSuccess: () => {
      refresh();
      notifyDone(text.closedNotice.title, text.closedNotice.message);
    },
    onError: (error) => {
      const problem = text.problem('close', problemCode(error));
      notifyProblem(problem.title, problem.message);
    },
  });

  const reopen = useMutation({
    mutationFn: () =>
      rawApi('POST', `${BUSINESS_DAYS_PATH}/${businessDayId}/reopen`, { reason: reason.trim() }),
    onSuccess: () => {
      refresh();
      setReason('');
      notifyDone(text.reopenedNotice.title, text.reopenedNotice.message);
    },
    onError: (error) => {
      const problem = text.problem('reopen', problemCode(error));
      notifyProblem(problem.title, problem.message);
    },
  });

  const sessions = summary.data?.sessions ?? [];
  const stillOpen = sessions.filter((s) => s.status === 'open').length;
  const counted = sessions.filter((s) => s.overShort !== null).length;
  const ordinaryTabs = summary.data?.closingWork.ordinaryTabs ?? 0;
  const awaitingPayment = summary.data?.closingWork.awaitingPayment ?? 0;
  const unresolvedGuestWork = ordinaryTabs + awaitingPayment;
  const closedOff = day?.status === 'closed';

  /*
   * Counting a drawer off is the whole reason this page exists, and a night closed with a drawer
   * still open is a night whose figures are not final however emphatically it is marked closed. The
   * server refuses it too, because the drawer close is the physical prerequisite for this
   * accounting close. Keeping the action disabled here lets the manager see what is left before a
   * request has to reject it.
   */
  const confirmClose = () =>
    modals.openConfirmModal({
      title: text.confirmTitle(formatDate(day?.businessDate, dateLocale)),
      children: (
        <Stack gap="sm">
          <Text size="sm">{text.confirmBody}</Text>
          <Text size="sm">{text.confirmReopen}</Text>
        </Stack>
      ),
      labels: { confirm: text.confirmAction, cancel: text.confirmCancel },
      onConfirm: () => close.mutate(),
    });

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Select
          label={text.businessDayLabel}
          placeholder={text.loading}
          data={dayOptions}
          value={businessDayId ?? null}
          onChange={(value) => {
            setChosenDayId(value);
            setReason('');
          }}
          allowDeselect={false}
          disabled={currentDay.isLoading || !day}
          w={320}
          maw="100%"
        />
        {day && !closedOff && (
          <Button
            variant="light"
            loading={close.isPending}
            disabled={!summary.isSuccess || !days.isSuccess || stillOpen > 0 || unresolvedGuestWork > 0}
            onClick={confirmClose}
          >
            {text.closeAction}
          </Button>
        )}
      </Group>

      {days.isError && (
        <Alert color="red" variant="light" title={text.daysLoadFailed.title}>
          <Text size="sm">{text.daysLoadFailed.message}</Text>
        </Alert>
      )}

      {day && (
        <div>
          <Group gap="xs">
            <Badge variant="light" color={enumColor('businessDayStatus', day.status)}>
              {text.status[day.status]}
            </Badge>
            <Text size="xs" c="dimmed">
              {closedOff && day.closedAt
                ? text.closedAt(formatDateTime(day.closedAt, dateLocale))
                : text.dayWindow}
            </Text>
          </Group>
          {reviewingPriorDay && (
            <Text size="xs" c="blue.8" mt={3}>
              {text.priorDayHint}
            </Text>
          )}
        </div>
      )}

      {day?.status === 'reopened' && day.reopenReason && (
        <Alert color="yellow" variant="light" title={text.reopenedTitle}>
          <Text size="sm">{day.reopenReason}</Text>
        </Alert>
      )}

      {stillOpen > 0 && (
        <Alert color="orange" variant="light" title={text.drawerCloseRequired}>
          <Text size="sm">{text.drawerCloseRequiredBody(stillOpen)}</Text>
        </Alert>
      )}

      {unresolvedGuestWork > 0 && (
        <Alert color="orange" variant="light" title={text.guestWorkRequired}>
          <Text size="sm">{text.guestWorkRequiredBody(ordinaryTabs, awaitingPayment)}</Text>
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Figure
          label={text.drawers}
          value={String(sessions.length)}
          hint={sessions.length === 0 ? text.noneOpened : text.drawerSummary(counted, stillOpen)}
        />
        {/*
          OVER AND SHORT, AND THE TWO NIGHTS IT MUST NOT ANSWER FOR.

          The server sums `overShortUsdMinor ?? 0` across every drawer on the night, so a drawer that
          is still trading — and has therefore never been counted — contributes nought and is
          indistinguishable from one that came out exact. On a night with no drawer counted off yet
          this card printed **Exact · Exact** under "Every drawer on this night added up": an
          all-clear on a night nobody has counted, which is the one wrong answer this figure can give,
          because it is the answer that stops somebody looking.

          Both corrections are made here rather than in the response, because nothing about the
          endpoint is wrong — `sessions` already carries a null `overShort` on every uncounted drawer,
          and this screen was throwing that away.
        */}
        <Figure
          label={text.variance}
          value={
            counted === 0 ? (
              <Text size="sm" c="dimmed">
                {text.nothingReconciled}
              </Text>
            ) : (
              <OverShortPair pair={summary.data?.overShortTotal ?? null} labels={varianceLabels} />
            )
          }
          hint={
            counted === 0
              ? text.nothingReconciledHint
              : counted < sessions.length
                ? text.someReconciledHint(counted)
                : text.allReconciledHint
          }
        />
        <Figure
          label={text.rounding}
          value={<MoneyPair pair={summary.data?.rounding ?? null} />}
          hint={text.roundingHint}
        />
      </SimpleGrid>

      {closedOff && (
        <Alert color="gray" variant="light" title={text.closedTitle}>
          <Stack gap="xs">
            <Text size="sm">{text.closedBody}</Text>
            <Textarea
              label={text.reopenReason}
              description={text.reopenReasonDescription}
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            <Group>
              <Button
                variant="light"
                color="yellow"
                // The server asks for three characters. Blocking it here as well means the owner
                // finds out before the request, not from a toast afterwards.
                disabled={reason.trim().length < 3 || reopen.isPending}
                loading={reopen.isPending}
                onClick={() => reopen.mutate()}
              >
                {text.reopenAction}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {(landed.data?.length ?? 0) > 0 && (
        <Card withBorder>
          <Text fw={600} size="sm">
            {text.lateTitle}
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            {text.lateDescription}
          </Text>
          <Stack gap={6}>
            {landed.data?.map((row) => (
              <Group key={`${row.at}-${row.what}`} justify="space-between" wrap="nowrap" gap="xs">
                <div>
                  <Text size="sm">{text.landedWhat(row.what)}</Text>
                  <Text size="xs" c="dimmed">
                    {text.lateMeta(
                      row.arrivedLate ? text.arrivedLate : text.recordedAfterClose,
                      row.by,
                      formatDateTime(row.at, dateLocale),
                    )}
                  </Text>
                </div>
                {/* An em dash rather than a zero: a drawer float genuinely moves no takings, and
                    printing "0" against it would read as a sale that came to nothing. */}
                <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
                  {row.amountMinor !== null && row.currency
                    ? formatMoney(row.amountMinor, row.currency)
                    : '—'}
                </Text>
              </Group>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
